import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar, Clock, Users, AlertCircle, CheckCircle2, MapPin, Utensils } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iframe-aware Stripe redirect.
 *
 * If we're inside an iframe (embed widget), open Stripe Checkout in a new
 * tab so the partner's site doesn't get hijacked. If we're on our own
 * page, replace the current location.
 */
function goToCheckout(url) {
  const isFramed = (() => { try { return window.top !== window.self; } catch { return true; } })();
  if (isFramed) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = url;
  }
}

// ============================================================
// SlotPicker — for kind=experience public pages
// ============================================================
export function SlotPicker({ event }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [guestCount, setGuestCount] = useState(2);
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (!event?.event_id) return;
    axios.get(`${API}/experiences/${event.event_id}/slots`)
      .then(res => setSlots(res.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [event?.event_id]);

  const dates = useMemo(() => Array.from(new Set(slots.map(s => s.date))).sort(), [slots]);
  const slotsForDate = useMemo(() => slots.filter(s => s.date === selectedDate), [slots, selectedDate]);

  useEffect(() => {
    if (!selectedDate && dates.length) setSelectedDate(dates[0]);
  }, [dates, selectedDate]);

  const book = async () => {
    if (!selectedSlot) return toast.error('Pick a time slot');
    if (!contact.name || !contact.email) return toast.error('Name and email are required');
    setSubmitting(true);
    try {
      // Stripe path: if the slot has a price, route through the Stripe
      // Checkout endpoint. The backend short-circuits price_cents == 0
      // to an instant confirm so free experiences still work.
      const isPaid = (selectedSlot.price_cents || 0) > 0;
      if (isPaid) {
        const res = await axios.post(`${API}/payments/experience-booking/checkout`, {
          event_id: event.event_id,
          slot_id: selectedSlot.slot_id,
          guest_count: guestCount,
          ...contact,
          origin_url: window.location.origin,
        });
        if (res.data?.free) {
          // Backend changed its mind (e.g. slot price was just edited to 0)
          setConfirmed({ booking_id: res.data.booking_id, guest_count: guestCount, amount_cents: 0, currency: 'usd', email: contact.email });
          toast.success('Booking confirmed!');
        } else {
          goToCheckout(res.data.checkout_url);
          // Keep submitting=true so the form stays disabled while Stripe loads.
          return;
        }
      } else {
        const res = await axios.post(`${API}/experiences/${event.event_id}/bookings`, {
          slot_id: selectedSlot.slot_id,
          guest_count: guestCount,
          ...contact,
        });
        setConfirmed(res.data);
        toast.success('Booking confirmed!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not complete booking');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading availability…</p>;

  if (confirmed) {
    return (
      <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-5 space-y-2" data-testid="slot-booking-confirmed">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <h3 className="font-semibold">You're booked.</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Confirmation #{confirmed.booking_id} · {confirmed.guest_count} guest{confirmed.guest_count === 1 ? '' : 's'} on {selectedSlot?.date} at {selectedSlot?.start_time}.
        </p>
        {confirmed.amount_cents > 0 && (
          <p className="text-sm font-medium">
            Total: ${(confirmed.amount_cents / 100).toFixed(2)} {confirmed.currency}
          </p>
        )}
        <p className="text-xs text-muted-foreground">A confirmation email is on its way to {confirmed.email}.</p>
      </div>
    );
  }

  if (!slots.length) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex gap-2 text-sm">
        <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">No upcoming slots yet</p>
          <p className="text-xs text-muted-foreground">The host hasn't published any dates. Check back soon.</p>
        </div>
      </div>
    );
  }

  const maxGuests = Math.min(
    selectedSlot?.available || 1,
    event?.max_group_size || 99
  );

  return (
    <div className="space-y-4" data-testid="slot-picker">
      {event?.meeting_point && (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm flex gap-2">
          <MapPin className="h-4 w-4 text-indigo-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Meeting point</p>
            <p className="text-xs text-muted-foreground whitespace-pre-line">{event.meeting_point}</p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Choose a date</Label>
        <div className="flex flex-wrap gap-2" data-testid="slot-date-picker">
          {dates.map(d => {
            const active = d === selectedDate;
            return (
              <button
                key={d}
                type="button"
                onClick={() => { setSelectedDate(d); setSelectedSlot(null); }}
                className={`px-3 py-2 rounded-lg border text-sm transition ${active ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:bg-muted/50'}`}
                data-testid={`slot-date-${d}`}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Choose a time</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="slot-time-picker">
          {slotsForDate.map(s => {
            const isFull = s.status === 'sold_out';
            const active = selectedSlot?.slot_id === s.slot_id;
            return (
              <button
                key={s.slot_id}
                type="button"
                disabled={isFull}
                onClick={() => setSelectedSlot(s)}
                className={[
                  'p-2.5 rounded-lg border text-left transition disabled:opacity-50 disabled:cursor-not-allowed',
                  active ? 'border-indigo-500 ring-2 ring-indigo-500/40 bg-indigo-500/5' : 'border-border hover:bg-muted/50',
                ].join(' ')}
                data-testid={`slot-time-${s.slot_id}`}
              >
                <div className="font-medium text-sm flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" /> {s.start_time}{s.end_time ? `–${s.end_time}` : ''}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {isFull ? 'Sold out' : `${s.available} spot${s.available === 1 ? '' : 's'} left`}
                  {s.price_cents > 0 && <> · ${(s.price_cents / 100).toFixed(0)}</>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selectedSlot && (
        <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Number of spots</Label>
              <Input
                type="number"
                min={1}
                max={maxGuests}
                value={guestCount}
                onChange={(e) => setGuestCount(Math.max(1, Math.min(maxGuests, parseInt(e.target.value || '1'))))}
                data-testid="slot-guest-count"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Your name *</Label>
              <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="slot-name" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="slot-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Phone</Label>
              <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
            </div>
          </div>
          <Button onClick={book} disabled={submitting} className="w-full" data-testid="slot-book-btn">
            {submitting ? 'Booking…' : `Book ${guestCount} spot${guestCount === 1 ? '' : 's'}${selectedSlot.price_cents > 0 ? ` · $${((selectedSlot.price_cents * guestCount) / 100).toFixed(2)}` : ''}`}
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ReservationPicker — for kind=reservation public pages
// ============================================================
export function ReservationPicker({ event }) {
  const [form, setForm] = useState({
    date: '',
    start_time: '',
    party_size: event?.party_size_min || 2,
    name: '',
    email: '',
    phone: '',
    seating_area: '',
    special_requests: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const partyMin = event?.party_size_min || 1;
  const partyMax = event?.party_size_max || 12;
  const threshold = event?.large_party_threshold;
  const perGuestDeposit = (event?.deposit_amount_cents || 0) / 100;
  const willHaveDeposit = !!(threshold && perGuestDeposit > 0 && form.party_size >= threshold);

  const submit = async () => {
    if (!form.date || !form.start_time) return toast.error('Pick a date and time');
    if (!form.name || !form.email) return toast.error('Name and email are required');
    setSubmitting(true);
    try {
      // Always route through the Stripe-aware payments endpoint. Backend
      // short-circuits when the party is below the deposit threshold so
      // small parties still instant-confirm without a checkout redirect.
      const res = await axios.post(`${API}/payments/reservation/checkout`, {
        ...form,
        event_id: event.event_id,
        origin_url: window.location.origin,
      });
      if (res.data?.free) {
        setConfirmed({
          reservation_id: res.data.reservation_id,
          date: form.date, start_time: form.start_time,
          party_size: form.party_size, email: form.email,
          deposit_amount_cents: 0,
        });
        toast.success('Reservation confirmed');
      } else {
        goToCheckout(res.data.checkout_url);
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not complete reservation');
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-5 space-y-2" data-testid="reservation-confirmed">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <h3 className="font-semibold">Table reserved.</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          #{confirmed.reservation_id} · Party of {confirmed.party_size} on {confirmed.date} at {confirmed.start_time}.
        </p>
        {confirmed.deposit_amount_cents > 0 && (
          <p className="text-sm font-medium text-amber-500">
            Hold required: ${(confirmed.deposit_amount_cents / 100).toFixed(2)} — your card will be charged for no-shows.
          </p>
        )}
        <p className="text-xs text-muted-foreground">A confirmation email is on its way to {confirmed.email}.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reservation-picker">
      <div className="rounded-lg border bg-muted/30 p-3 text-sm flex gap-2">
        <Utensils className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Reserve a table</p>
          <p className="text-xs text-muted-foreground">
            Parties of {partyMin}–{partyMax}. {threshold && perGuestDeposit > 0 ? `Groups of ${threshold}+ pay a $${perGuestDeposit.toFixed(2)}/guest hold.` : ''}
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" /> Date</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="res-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1"><Clock className="h-3 w-3" /> Time</Label>
          <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="res-time" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1"><Users className="h-3 w-3" /> Party size</Label>
          <Input
            type="number"
            min={partyMin}
            max={partyMax}
            value={form.party_size}
            onChange={(e) => setForm({ ...form, party_size: Math.max(partyMin, Math.min(partyMax, parseInt(e.target.value || partyMin))) })}
            data-testid="res-party-size"
          />
        </div>
      </div>

      {(event?.seating_areas || []).length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs">Seating preference</Label>
          <div className="flex flex-wrap gap-2">
            {event.seating_areas.map(a => (
              <button
                key={a}
                type="button"
                onClick={() => setForm({ ...form, seating_area: a === form.seating_area ? '' : a })}
                className={`px-3 py-1.5 rounded-full text-xs border transition ${form.seating_area === a ? 'border-amber-500 bg-amber-500/10' : 'border-border hover:bg-muted/50'}`}
                data-testid={`res-seating-${a.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Your name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="res-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Email *</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="res-email" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Special requests</Label>
        <Textarea
          rows={2}
          value={form.special_requests}
          onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
          placeholder="Birthday, allergies, accessibility, etc."
          data-testid="res-special-requests"
        />
      </div>

      {willHaveDeposit && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-300">
          A ${(perGuestDeposit * form.party_size).toFixed(2)} hold will be placed on your card for parties of {threshold}+. Refunded after dining.
        </div>
      )}

      <Button onClick={submit} disabled={submitting} className="w-full" data-testid="res-book-btn">
        {submitting ? 'Reserving…' : `Reserve a table for ${form.party_size}`}
      </Button>
    </div>
  );
}
